import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'react-toastify';
import api from '../services/api';

const NavbarSpacer = styled.div`
  height: 120px;
  @media (min-width: 768px) {
    height: 140px;
  }
  @media (min-width: 1024px) {
    height: 160px;
  }
`;

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #fff;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  padding: 40px 16px;
  
  @media (min-width: 768px) {
    padding: 60px 24px;
  }
  
  @media (min-width: 1024px) {
    padding: 80px 24px;
  }
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 20px;
  text-align: center;
  color: #22c55e;
  
  @media (min-width: 768px) {
    font-size: 48px;
  }
`;

const GiftCardCodeBox = styled.div`
  background: #000;
  color: #fff;
  padding: 30px;
  border-radius: 8px;
  text-align: center;
  margin: 24px 0;
`;

const CodeLabel = styled.p`
  font-size: 14px;
  color: #999;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const CodeValue = styled.p`
  font-size: 32px;
  font-weight: 700;
  letter-spacing: 4px;
  font-family: 'Courier New', monospace;
  margin: 0;
  
  @media (min-width: 768px) {
    font-size: 40px;
    letter-spacing: 6px;
  }
`;

const AmountDisplay = styled.div`
  text-align: center;
  margin: 24px 0;
  font-size: 24px;
  font-weight: 600;
`;

const GiftCardSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [giftCard, setGiftCard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const confirmPurchase = async () => {
      const sessionId = searchParams.get('session_id');

      if (!sessionId) {
        toast.error('Invalid session');
        navigate('/gift-cards');
        return;
      }

      try {
        const response = await api.post('/api/giftcards/confirm-purchase', {
          sessionId,
        });

        if (response.data.success) {
          setGiftCard(response.data.giftCard);
          toast.success('Gift card purchase confirmed!');
        } else {
          toast.error(response.data.error || 'Failed to confirm purchase');
          navigate('/gift-cards');
        }
      } catch (error) {
        toast.error(error.response?.data?.error || 'Failed to confirm purchase');
        navigate('/gift-cards');
      } finally {
        setLoading(false);
      }
    };

    confirmPurchase();
  }, [searchParams, navigate]);

  if (loading) {
    return (
      <PageContainer>
        <NavbarSpacer />
        <ContentWrapper>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>Confirming your purchase...</p>
          </div>
        </ContentWrapper>
      </PageContainer>
    );
  }

  if (!giftCard) {
    return null;
  }

  return (
    <PageContainer>
      <NavbarSpacer />
      <ContentWrapper>
        <Title>✓ Purchase Successful!</Title>

        <Card>
          <CardHeader>
            <CardTitle>Your Gift Card</CardTitle>
          </CardHeader>
          <CardContent>
            <GiftCardCodeBox>
              <CodeLabel>Gift Card Code</CodeLabel>
              <CodeValue>{giftCard.code}</CodeValue>
            </GiftCardCodeBox>

            <AmountDisplay>
              Value: £{giftCard.amount.toFixed(2)}
            </AmountDisplay>

            <div style={{ background: '#f0f9ff', padding: '20px', borderRadius: '8px', marginTop: '24px' }}>
              <p style={{ marginBottom: '12px', fontWeight: 600 }}>What's Next?</p>
              <p style={{ fontSize: '14px', lineHeight: '1.6', marginBottom: '8px' }}>
                • Your gift card code has been sent to your email
              </p>
              <p style={{ fontSize: '14px', lineHeight: '1.6', marginBottom: '8px' }}>
                • Use this code at checkout to apply the discount
              </p>
              <p style={{ fontSize: '14px', lineHeight: '1.6', marginBottom: '8px' }}>
                • Gift cards never expire and can be used on any purchase
              </p>
            </div>
            
            <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', padding: '16px', borderRadius: '8px', marginTop: '20px' }}>
              <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#991b1b', fontWeight: 600, margin: 0 }}>
                ⚠️ Important: Gift cards are non-refundable once the transaction is successful. All sales are final.
              </p>
            </div>
          </CardContent>
        </Card>

        <div style={{ marginTop: '32px', display: 'flex', gap: '16px', justifyContent: 'center' }}>
          <Button variant="outline" onClick={() => navigate('/shop')}>
            Continue Shopping
          </Button>
          <Button onClick={() => navigate('/gift-cards')}>
            Purchase Another
          </Button>
        </div>
      </ContentWrapper>
    </PageContainer>
  );
};

export default GiftCardSuccessPage;

